import { logger } from "@elizaos/core";
import type { AgentRuntime, UUID, Project as ProjectType, ProjectAgent, Character } from "@elizaos/core";
import { resolve } from "node:path";
import { v4 as uuidv4 } from "uuid";
import * as fs from "node:fs";
import path from "node:path";

/**
 * Interface for a project module that can be loaded.
 */
interface ProjectModule {
  agents?: ProjectAgent[];
  character?: Character;
  init?: (runtime: any) => Promise<void>;
  [key: string]: any;
}

/**
 * Interface for a loaded project.
 */
export interface Project {
  agents: ProjectAgent[];
  dir: string;
}

export interface LoadedProject {
  runtimes: AgentRuntime[];
  path: string;
  agents: ProjectAgent[];
}

/**
 * Loads a project from the specified directory.
 * @param {string} dir - The directory to load the project from.
 * @returns {Promise<Project>} A promise that resolves to the loaded project.
 */
export async function loadProject(dir: string): Promise<Project> {
  try {
    // Try to find the project's entry point
    const entryPoints = [
      path.join(dir, "src/index.ts"),
      path.join(dir, "src/index.js"),
      path.join(dir, "dist/index.js"),
      path.join(dir, "index.ts"),
      path.join(dir, "index.js"),
    ];

    let projectModule: ProjectModule | null = null;
    for (const entryPoint of entryPoints) {
      if (fs.existsSync(entryPoint)) {
        try {
          const importPath = path.resolve(entryPoint);
          projectModule = await import(importPath) as ProjectModule;
          logger.info(`Loaded project from ${entryPoint}`);
          break;
        } catch (error) {
          logger.warn(`Failed to import project from ${entryPoint}:`, error);
        }
      }
    }

    if (!projectModule) {
      throw new Error("Could not find project entry point");
    }

    // Extract agents from the project module
    const agents: ProjectAgent[] = [];

    // Look for exported agents
    for (const [key, value] of Object.entries(projectModule)) {
      if (key === "default" && value && typeof value === "object") {
        // If it's a default export and has agents property, use those
        if (Array.isArray((value as ProjectModule).agents)) {
          agents.push(...((value as ProjectModule).agents as ProjectAgent[]));
        } else if ((value as ProjectModule).character && (value as ProjectModule).init) {
          // If it's a single agent, add it
          agents.push(value as ProjectAgent);
        }
      } else if (
        value &&
        typeof value === "object" &&
        (value as ProjectModule).character &&
        (value as ProjectModule).init
      ) {
        // If it's a named export that looks like an agent, add it
        agents.push(value as ProjectAgent);
      }
    }

    if (agents.length === 0) {
      throw new Error("No agents found in project");
    }

    // Create and return the project object
    const project: Project = {
      agents,
      dir,
    };

    return project;
  } catch (error) {
    logger.error("Error loading project:", error);
    throw error;
  }
} 